import { touchUserContextData } from "../utils/userHelper"

export function getRequest(path, token = "", version = "v2", signal = null, userContext = null, extraHeaders = {}) {
  const base = version === "v1" ? process.env.REACT_APP_API_V1_BASE_URL : process.env.REACT_APP_API_BASE_URL
  const payload = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": process.env.REACT_APP_API_KEY,
      "X-AUTH-TOKEN": token,
      ...extraHeaders
    }
  }
  if (signal !== null) payload["signal"] = signal
  return fetch(`${base}${path}`, payload).then((response) => {
    if (signal !== null && signal.aborted) {
      return false
    } else {
      if (userContext !== null) touchUserContextData(userContext)
      return handleResponse(response)
    }
  })
}

export function getFileRequest(url, token = "") {
  return fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": process.env.REACT_APP_API_KEY,
      "X-AUTH-TOKEN": token
    }
  }).then((response) => handleFileErrors(response))
}

export function postRequest(path, token = "", body = "", version = "v2", userContext = null) {
  const base = version === "v1" ? process.env.REACT_APP_API_V1_BASE_URL : process.env.REACT_APP_API_BASE_URL
  return fetch(`${base}${path}`, {
    method: "POST",
    body: body,
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": process.env.REACT_APP_API_KEY,
      "X-AUTH-TOKEN": token
    }
  }).then((response) => {
    if (userContext !== null) touchUserContextData(userContext)
    return handleResponse(response)
  })
}

export function patchRequest(path, token = "", body = "", version = "v2", userContext = null) {
  const base = version === "v1" ? process.env.REACT_APP_API_V1_BASE_URL : process.env.REACT_APP_API_BASE_URL
  return fetch(`${base}${path}`, {
    method: "PATCH",
    body: body,
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": process.env.REACT_APP_API_KEY,
      "X-AUTH-TOKEN": token
    }
  }).then((response) => {
    if (userContext !== null) touchUserContextData(userContext)
    return handleResponse(response)
  })
}

export function patchRequestNoContentType(path, token = "", body = "", version = "v2", userContext = null) {
  const base = version === "v1" ? process.env.REACT_APP_API_V1_BASE_URL : process.env.REACT_APP_API_BASE_URL
  return fetch(`${base}${path}`, {
    method: "PATCH",
    body: body,
    headers: {
      "X-API-KEY": process.env.REACT_APP_API_KEY,
      "X-AUTH-TOKEN": token
    }
  }).then((response) => {
    if (userContext !== null) touchUserContextData(userContext)
    return handleResponse(response)
  })
}

export function deleteRequest(path, token = "", version = "v2", userContext = null) {
  const base = version === "v1" ? process.env.REACT_APP_API_V1_BASE_URL : process.env.REACT_APP_API_BASE_URL
  return fetch(`${base}${path}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": process.env.REACT_APP_API_KEY,
      "X-AUTH-TOKEN": token
    }
  }).then((response) => {
    if (userContext !== null) touchUserContextData(userContext)
    return handleResponse(response)
  })
}

function handleResponse(response) {
  try {
    return response.json().then((result) => {
      if (shouldInterceptResponse(response)) {
        interceptResponse(response)
      }

      if (response.ok) {
        return result
      } else {
        return handleErrors(result)
      }
    })
  } catch (error) {
    console.error(error)
    throw new Error(response.statusText)
  }
}

function shouldInterceptResponse(response) {
  // process unless we are hitting sign_in
  const url = response?.url
  if (!url) return true
  if (url.includes("/sessions/sign_in")) return false
  // A Campfront handoff shows its own errors; bouncing to login would lose the one-time code.
  if (url.includes("/sign_in_with_token/login")) return false
  return response.status === 401 || response.status === 413
}

function interceptResponse(response) {
  switch (response.status) {
    case 401:
      intercept401Response(response)
      break
    case 413:
      intercept413Response()
      break
    default:
      break
  }
}

function intercept401Response(response) {
  let authState = {
    isAuthenticated: false,
    user: {}
  }

  document.cookie = `authState=; path=/; max-age=0;`
  sessionStorage["authState"] = JSON.stringify(authState)
  sessionStorage["placementsQueryString"] = null
  window.location.href = `/login?code=${response.status}&redirect_to=${encodeURIComponent(window.location.pathname + window.location.search)}`
}

function intercept413Response() {
  throw new Error("File too large. Please try again with file smaller than 10MB.")
}

function handleErrors(result) {
  throw new Error(extractErrorFromResult(result))
}

export function extractErrorFromResult(result) {
  try {
    if ("error" in result) {
      if (typeof result["error"] === "object" && result["error"] !== null) return result["error"]["message"]
      else return result["error"]
    } else if ("errors" in result && Array.isArray(result["errors"])) {
      if (typeof result["errors"][0] === "object") return result["errors"].map(({ message }) => message).join(", ")
      else return result["errors"].join(", ")
    } else if ("message" in result) {
      return result["message"]
    } else if ("error_message" in result) {
      // The API's own 404 and 500 bodies use this key, so without it every
      // not-found and server error reached the user as "Something went wrong".
      return result["error_message"]
    } else {
      return "Something went wrong"
    }
  } catch (error) {
    console.error(error)
    return "Something went wrong"
  }
}

function handleFileErrors(response) {
  console.log(response)
  if (response.ok) return response.blob()
  else throw new Error(response.statusText)
}

export function prepareParams(object = {}, whitelist = []) {
  return Object.keys(object)
    .filter((key) => whitelist.includes(key))
    .reduce((obj, key) => {
      obj[key] = object[key]
      return obj
    }, {})
}

export function blockParams(object = {}, blocklist = []) {
  return Object.keys(object)
    .filter((key) => !blocklist.includes(key))
    .reduce((obj, key) => {
      obj[key] = object[key]
      return obj
    }, {})
}

export async function getPoxyToken(access_token) {
  const now = Date.now()
  const cached = localStorage.getItem("poxy_token")
  if (cached) {
    const { token, expiry } = JSON.parse(cached)
    if (token && expiry && now < expiry) {
      return token
    }
  }

  // Fetch new token
  const data = await getRequest("/poxy_token", access_token, "v1")
  const token = data.token
  // Try to get expiry from JWT, fallback to 24h
  let expiry = getJwtExpiry(token) || now + 24 * 60 * 60 * 1000
  localStorage.setItem("poxy_token", JSON.stringify({ token, expiry }))
  return token
}

function getJwtExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]))
    return payload.exp * 1000 // convert to ms
  } catch {
    return null
  }
}
